import { useMemo, useRef } from "react";
import {
    Typography,
    Grid,
    Box,
    Paper,
    Card,
    CardContent,
    IconButton,
    Tooltip,
    Alert,
    Button
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useAtom } from "jotai";
import { format } from "date-fns";

import { companyIdAtom, isMbxUserAdmin, companyMasterTz } from "../atoms/atoms";
import { useDashboardData } from "../hooks/useDashboardData";
import { prepareComparisonChartData } from "../modules/DashboardService";
import { getCurrencySymbolForDashboard } from "../utils/dashboardUtils";
import {
    GrowthIndicator,
    DashboardSkeleton,
    ComparisonChart
} from "../components/dashboard";
import { isMbxFreeTrialUserAtom } from "../atoms/atoms.js";
import QuickStartGuide from "../components/QuickStartGuide.js";
// ============================================================================
// CONFIGURATION
// ============================================================================

const DASHBOARD_CONFIG = {
    showGrowthIndicators: true, // Toggle growth % badges
    showTips: true, // Toggle tips card
    enablePdfExport: true // Toggle PDF button
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get current month name
 */
function getCurrentMonthName() {
    return format(new Date(), "MMMM");
}

/**
 * Get current year
 */
function getCurrentYear() {
    return new Date().getFullYear();
}

/**
 * Calculate total revenue (net) by currency
 * Net = (Booked + Additional) - Refunds
 */
function calculateTotalRevenue(clientChargeData, additionalChargeData) {
    const result = {};

    // Get booked and refunds from client charges
    const hasCurrencyData =
        clientChargeData?.byCurrency &&
        Object.keys(clientChargeData.byCurrency).length > 0;

    if (hasCurrencyData) {
        Object.entries(clientChargeData.byCurrency).forEach(
            ([currency, data]) => {
                const booked = data?.booked ?? 0;
                const refunds = data?.refunds ?? 0;
                result[currency] = booked - refunds;
            }
        );
    }

    // Add additional charges with multi-currency support
    if (additionalChargeData?.additionalChargesByCurrency) {
        Object.entries(
            additionalChargeData.additionalChargesByCurrency
        ).forEach(([currency, amount]) => {
            result[currency] = (result[currency] || 0) + amount;
        });
    } else if (additionalChargeData?.nonTipCharges > 0) {
        // Fallback for backward compatibility
        const primaryCurrency = Object.keys(result)[0] || "USD";
        result[primaryCurrency] =
            (result[primaryCurrency] || 0) + additionalChargeData.nonTipCharges;
    }

    // Ensure we always return at least a default value
    if (Object.keys(result).length === 0) {
        return { USD: 0 };
    }

    return result;
}

// ============================================================================
// SECTION COMPONENTS
// ============================================================================

/**
 * Grouped metric card showing multiple related values as rows
 */
function GroupedMetricCard({ title, icon, rows, accentColor }) {
    return (
        <Card
            sx={{
                height: "100%",
                borderRadius: "12px",
                border: "1px solid #e8e8e8",
                borderLeft: (theme) =>
                    `4px solid ${accentColor || theme.palette.primary.main}`,
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                backgroundColor: "#fff"
            }}
        >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 1.5,
                        pb: 1,
                        borderBottom: "1px solid #f0f0f0"
                    }}
                >
                    {icon && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 32,
                                height: 32,
                                borderRadius: "8px",
                                backgroundColor: (theme) =>
                                    accentColor
                                        ? `${accentColor}15`
                                        : theme.palette.primary.light,
                                color: (theme) =>
                                    accentColor || theme.palette.primary.main
                            }}
                        >
                            {icon}
                        </Box>
                    )}
                    <Typography
                        sx={{
                            color: "#333",
                            fontSize: "0.9rem",
                            fontWeight: 600
                        }}
                    >
                        {title}
                    </Typography>
                </Box>

                {/* Rows */}
                {rows.map((row, index) => (
                    <Box
                        key={row.label}
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 0.75,
                            borderBottom:
                                index < rows.length - 1
                                    ? "1px solid #f5f5f5"
                                    : "none"
                        }}
                    >
                        <Typography
                            sx={{
                                color: row.highlight ? "#333" : "#666",
                                fontSize: "0.8rem",
                                fontWeight: row.highlight ? 600 : 400
                            }}
                        >
                            {row.label}
                        </Typography>
                        <Typography
                            sx={{
                                fontWeight: row.highlight ? 700 : 500,
                                fontSize: row.highlight ? "0.95rem" : "0.85rem",
                                color: "#333"
                            }}
                        >
                            {row.value}
                        </Typography>
                    </Box>
                ))}
            </CardContent>
        </Card>
    );
}

/**
 * Format currency value for display with proper currency symbol
 */
function formatCurrency(value, currency = "USD") {
    const num = typeof value === "number" ? value : 0;
    const symbol = getCurrencySymbolForDashboard(currency);
    return `${symbol}${num.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
}

/**
 * Get primary currency from byCurrency object (first currency found)
 */
function getPrimaryCurrency(byCurrency) {
    if (!byCurrency || Object.keys(byCurrency).length === 0) {
        return "USD";
    }
    return Object.keys(byCurrency)[0];
}

/**
 * Get primary currency value from byCurrency object
 */
function getPrimaryCurrencyValue(byCurrency, field = "net") {
    if (!byCurrency || Object.keys(byCurrency).length === 0) {
        return 0;
    }
    // Sum all currencies for now (could show primary currency instead)
    return Object.values(byCurrency).reduce(
        (sum, curr) => sum + (curr?.[field] ?? 0),
        0
    );
}

/**
 * Metrics section (Today or Current Month) - Compact grouped version
 */
function MetricsSection({ title, clientCharges, additionalCharges }) {
    const currency = getPrimaryCurrency(clientCharges?.byCurrency);
    const bookedRevenue = getPrimaryCurrencyValue(
        clientCharges?.byCurrency,
        "booked"
    );
    const refundsAmount = getPrimaryCurrencyValue(
        clientCharges?.byCurrency,
        "refunds"
    );
    const additionalChargesAmount = additionalCharges?.nonTipCharges || 0;
    const tipsAmount = additionalCharges?.tips || 0;
    const grossRevenue = bookedRevenue + additionalChargesAmount;
    const netRevenue = grossRevenue - refundsAmount;

    return (
        <Paper
            sx={{
                p: 2.5,
                mb: 2,
                borderRadius: "12px",
                border: "1px solid #e0e0e0",
                boxShadow: "none"
            }}
            className="dashboard-section"
        >
            <Typography
                sx={{
                    mb: 2,
                    fontWeight: 600,
                    fontSize: "1rem",
                    color: "#333"
                }}
            >
                {title}
            </Typography>
            <Grid container spacing={2}>
                {/* Revenue Summary */}
                <Grid item xs={12} sm={6} md={4}>
                    <GroupedMetricCard
                        title="Revenue"
                        icon={<AttachMoneyIcon fontSize="small" />}
                        rows={[
                            {
                                label: "Booked",
                                value: formatCurrency(bookedRevenue, currency)
                            },
                            {
                                label: "Additional charges",
                                value: formatCurrency(
                                    additionalChargesAmount,
                                    currency
                                )
                            },
                            {
                                label: "Gross",
                                value: formatCurrency(grossRevenue, currency)
                            },
                            {
                                label: "Refunds",
                                value: formatCurrency(refundsAmount, currency)
                            },
                            {
                                label: "Net",
                                value: formatCurrency(netRevenue, currency),
                                highlight: true
                            }
                        ]}
                    />
                </Grid>

                {/* Tips & Activity */}
                <Grid item xs={12} sm={6} md={4}>
                    <GroupedMetricCard
                        title="Tips & Activity"
                        icon={<VolunteerActivismIcon fontSize="small" />}
                        accentColor="#4caf50"
                        rows={[
                            {
                                label: "Tips",
                                value: formatCurrency(tipsAmount, currency),
                                highlight: true
                            },
                            {
                                label: "Orders",
                                value: (
                                    clientCharges?.totalOrders || 0
                                ).toLocaleString()
                            },
                            {
                                label: "Paying Clients",
                                value: (
                                    clientCharges?.totalClients || 0
                                ).toLocaleString()
                            }
                        ]}
                    />
                </Grid>

                {/* Grand Total */}
                <Grid item xs={12} sm={6} md={4}>
                    <Card
                        sx={{
                            height: "100%",
                            borderRadius: "12px",
                            border: "1px solid #e8e8e8",
                            borderLeft: "4px solid #2196f3",
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                            backgroundColor: "#fafafa",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center"
                        }}
                    >
                        <CardContent
                            sx={{
                                p: 2,
                                "&:last-child": { pb: 2 },
                                textAlign: "center"
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                    mb: 1
                                }}
                            >
                                <TrendingUpIcon
                                    sx={{
                                        color: "#2196f3",
                                        fontSize: "1.2rem"
                                    }}
                                />
                                <Typography
                                    sx={{
                                        color: "#666",
                                        fontSize: "0.85rem",
                                        fontWeight: 500
                                    }}
                                >
                                    Grand Total
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontSize: "1.75rem",
                                    color: "#333",
                                    lineHeight: 1.2
                                }}
                            >
                                {formatCurrency(
                                    netRevenue + tipsAmount,
                                    currency
                                )}
                            </Typography>
                            <Typography
                                sx={{
                                    color: "#999",
                                    fontSize: "0.7rem",
                                    mt: 0.5
                                }}
                            >
                                Net revenue + Tips
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Paper>
    );
}

/**
 * Revenue row component for YearSummaryCard
 */
function RevenueRow({ label, value, currency = "USD", highlight = false }) {
    const symbol = getCurrencySymbolForDashboard(currency);
    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: 0.75,
                borderBottom: "1px solid #e8e8e8"
            }}
        >
            <Typography
                sx={{
                    color: highlight ? "#333" : "#666",
                    fontSize: "0.875rem",
                    fontWeight: highlight ? 600 : 400
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontWeight: highlight ? 700 : 500,
                    fontSize: highlight ? "1rem" : "0.875rem",
                    color: "#333"
                }}
            >
                {symbol}
                {value.toLocaleString("en-US", { minimumFractionDigits: 2 })}
            </Typography>
        </Box>
    );
}

/**
 * Year summary card - Shows 5 revenue rows only (Booked, Additional, Gross, Refunds, Net)
 */
function YearSummaryCard({ title, data, growth, showGrowth }) {
    // Helper to safely check if byCurrency has data
    const hasCurrencyData =
        data?.clientCharges?.byCurrency &&
        Object.keys(data.clientCharges.byCurrency).length > 0;

    const currency = getPrimaryCurrency(data?.clientCharges?.byCurrency);

    const bookedRevenue = hasCurrencyData
        ? Object.values(data.clientCharges.byCurrency).reduce(
              (sum, curr) => sum + (curr?.booked ?? 0),
              0
          )
        : 0;

    const refundsAmount = hasCurrencyData
        ? Object.values(data.clientCharges.byCurrency).reduce(
              (sum, curr) => sum + (curr?.refunds ?? 0),
              0
          )
        : 0;

    const additionalCharges = data?.additionalCharges?.nonTipCharges || 0;
    const grossRevenue = bookedRevenue + additionalCharges;
    const netRevenue = grossRevenue - refundsAmount;

    return (
        <Paper
            sx={{
                p: 2.5,
                backgroundColor: "#fafafa",
                borderRadius: "12px",
                border: "1px solid #e0e0e0",
                boxShadow: "none",
                height: "100%"
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1.5
                }}
            >
                <Typography
                    sx={{ fontWeight: 600, fontSize: "0.95rem", color: "#333" }}
                >
                    {title}
                </Typography>
                {showGrowth && growth && (
                    <GrowthIndicator
                        current={growth.current}
                        previous={growth.previous}
                        showIndicator={DASHBOARD_CONFIG.showGrowthIndicators}
                        size="small"
                    />
                )}
            </Box>

            {/* 5 Revenue Rows */}
            <RevenueRow
                label="Booked"
                value={bookedRevenue}
                currency={currency}
            />
            <RevenueRow
                label="Additional charges"
                value={additionalCharges}
                currency={currency}
            />
            <RevenueRow
                label="Gross"
                value={grossRevenue}
                currency={currency}
            />
            <RevenueRow
                label="Refunds"
                value={refundsAmount}
                currency={currency}
            />
            <RevenueRow
                label="Net"
                value={netRevenue}
                currency={currency}
                highlight
            />
        </Paper>
    );
}

/**
 * Year comparison section with chart
 */
function YearComparisonSection({ ytdData, lastYearData, growth, metadata }) {
    const currentYear = metadata?.currentYear || getCurrentYear();
    const lastYear = metadata?.lastYear || currentYear - 1;
    const currency = getPrimaryCurrency(ytdData?.clientCharges?.byCurrency);

    // Prepare chart data
    const chartData = useMemo(() => {
        if (!ytdData?.monthlyData || !lastYearData?.monthlyData) {
            return [];
        }
        return prepareComparisonChartData(
            ytdData.monthlyData,
            lastYearData.monthlyData,
            currentYear,
            lastYear
        );
    }, [ytdData, lastYearData, currentYear, lastYear]);

    // Calculate totals for growth indicator with safe checks for empty objects
    // Net = (Booked + Additional) - Refunds
    const ytdHasData =
        ytdData?.clientCharges?.byCurrency &&
        Object.keys(ytdData.clientCharges.byCurrency).length > 0;

    const lastYearHasData =
        lastYearData?.clientCharges?.byCurrency &&
        Object.keys(lastYearData.clientCharges.byCurrency).length > 0;

    const ytdBooked = ytdHasData
        ? Object.values(ytdData.clientCharges.byCurrency).reduce(
              (sum, curr) => sum + (curr?.booked ?? 0),
              0
          )
        : 0;

    const ytdRefunds = ytdHasData
        ? Object.values(ytdData.clientCharges.byCurrency).reduce(
              (sum, curr) => sum + (curr?.refunds ?? 0),
              0
          )
        : 0;

    const lastYearBooked = lastYearHasData
        ? Object.values(lastYearData.clientCharges.byCurrency).reduce(
              (sum, curr) => sum + (curr?.booked ?? 0),
              0
          )
        : 0;

    const lastYearRefunds = lastYearHasData
        ? Object.values(lastYearData.clientCharges.byCurrency).reduce(
              (sum, curr) => sum + (curr?.refunds ?? 0),
              0
          )
        : 0;

    // Include additional charges in total revenue for growth calculation
    const ytdAdditionalCharges = ytdData?.additionalCharges?.nonTipCharges || 0;
    const lastYearAdditionalCharges =
        lastYearData?.additionalCharges?.nonTipCharges || 0;

    // Net = (Booked + Additional) - Refunds
    const ytdTotalRevenue = ytdBooked + ytdAdditionalCharges - ytdRefunds;
    const lastYearTotalRevenue =
        lastYearBooked + lastYearAdditionalCharges - lastYearRefunds;

    return (
        <Paper
            sx={{
                p: 3,
                borderRadius: "12px",
                border: "1px solid #e0e0e0",
                boxShadow: "none"
            }}
            className="dashboard-section"
        >
            <Typography
                sx={{
                    mb: 2,
                    fontWeight: 600,
                    fontSize: "1rem",
                    color: "#333"
                }}
            >
                Year Comparison
            </Typography>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <YearSummaryCard
                        title={`${currentYear} (Year to Date)`}
                        data={ytdData}
                        growth={{
                            current: ytdTotalRevenue,
                            previous: lastYearTotalRevenue
                        }}
                        showGrowth={false}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <YearSummaryCard
                        title={`${lastYear} (Full Year)`}
                        data={lastYearData}
                        showGrowth={false}
                    />
                </Grid>
            </Grid>

            {/* Comparison Chart */}
            {chartData.length > 0 && (
                <ComparisonChart
                    data={chartData}
                    currentYear={currentYear}
                    lastYear={lastYear}
                    showGrowthIndicator={DASHBOARD_CONFIG.showGrowthIndicators}
                    height={400}
                    currency={currency}
                />
            )}
        </Paper>
    );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function DashboardForm() {
    const [isAdmin] = useAtom(isMbxUserAdmin);
    const [companyId] = useAtom(companyIdAtom);
    const [timezone] = useAtom(companyMasterTz);
    const dashboardRef = useRef(null);
    const [isMbxUserFreeTrial] = useAtom(isMbxFreeTrialUserAtom);

    // Fetch dashboard data using company's master timezone
    const { data, isLoading, isError, error, refetch, isFetching } =
        useDashboardData(companyId, timezone);

    // Handle PDF export using browser print
    const handleExportPDF = () => {
        window.print();
    };

    // Handle manual refresh
    const handleRefresh = () => {
        refetch();
    };

    // Admin access check
    if (!isAdmin) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning">
                    Access denied. Admin privileges are required to view the
                    dashboard.
                </Alert>
            </Box>
        );
    }

    // Loading state
    if (isLoading) {
        return <DashboardSkeleton />;
    }

    // Error state
    if (isError) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert
                    severity="error"
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={handleRefresh}
                        >
                            Retry
                        </Button>
                    }
                >
                    Failed to load dashboard data.{" "}
                    {error?.message || "Please try again."}
                </Alert>
            </Box>
        );
    }

    const currentMonth = getCurrentMonthName();
    const currentYear = getCurrentYear();

    return (
        <>
            {isMbxUserFreeTrial && (
                <Grid container mb={8} sx={{ flexDirection: "column" }}>
                    <Grid item xs={10}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: "24px",
                                marginBottom: "10px"
                            }}
                            noWrap
                        >
                            QuickStart Checklist
                        </Typography>
                    </Grid>
                    <Grid item>
                        <QuickStartGuide />
                    </Grid>
                </Grid>
            )}
            {/* Print Styles */}
            <style>
                {`
                    @media print {
                        .no-print { display: none !important; }
                        .dashboard-section {
                            break-inside: avoid;
                            page-break-inside: avoid;
                        }
                        body {
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                    }
                `}
            </style>

            <Box ref={dashboardRef} sx={{ p: 3 }}>
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3
                    }}
                >
                    <Typography
                        variant="h4"
                        sx={{ fontSize: "24px", fontWeight: 600 }}
                    >
                        Dashboard
                    </Typography>
                    <Box className="no-print" sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="Refresh data">
                            <IconButton
                                onClick={handleRefresh}
                                disabled={isFetching}
                                sx={{
                                    backgroundColor: "grey.100",
                                    "&:hover": { backgroundColor: "grey.200" }
                                }}
                            >
                                <RefreshIcon
                                    sx={{
                                        animation: isFetching
                                            ? "spin 1s linear infinite"
                                            : "none",
                                        "@keyframes spin": {
                                            "0%": { transform: "rotate(0deg)" },
                                            "100%": {
                                                transform: "rotate(360deg)"
                                            }
                                        }
                                    }}
                                />
                            </IconButton>
                        </Tooltip>
                        {DASHBOARD_CONFIG.enablePdfExport && (
                            <Tooltip title="Export to PDF">
                                <IconButton
                                    onClick={handleExportPDF}
                                    sx={{
                                        backgroundColor: "grey.100",
                                        "&:hover": {
                                            backgroundColor: "grey.200"
                                        }
                                    }}
                                >
                                    <PictureAsPdfIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>

                {/* Last updated indicator */}
                {data?.metadata?.fetchedAt && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 2 }}
                        className="no-print"
                    >
                        Last updated:{" "}
                        {format(
                            new Date(data.metadata.fetchedAt),
                            "MMM d, yyyy h:mm a"
                        )}
                    </Typography>
                )}

                {/* Today Section */}
                <MetricsSection
                    title="Today"
                    clientCharges={data?.today?.clientCharges}
                    additionalCharges={data?.today?.additionalCharges}
                />

                {/* Current Month Section */}
                <MetricsSection
                    title={`${currentMonth} ${currentYear}`}
                    clientCharges={data?.currentMonth?.clientCharges}
                    additionalCharges={data?.currentMonth?.additionalCharges}
                />

                {/* Year Comparison Section */}
                <YearComparisonSection
                    ytdData={data?.ytd}
                    lastYearData={data?.lastYear}
                    growth={data?.growth}
                    metadata={data?.metadata}
                />
            </Box>
        </>
    );
}

export default DashboardForm;
